import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, RefreshCw, Sparkles } from 'lucide-react';
import { useI18n } from '@/i18n/I18nProvider';
import { cn } from '@/shared/lib/cn';
import { hexToRgba } from '@/shared/lib/nav-accent';
import type { HeroInsightData, HeroQuickAction } from './types';

const INSIGHT_ICON = { info: Sparkles, success: CheckCircle2, warning: AlertTriangle, danger: AlertTriangle } as const;
const INSIGHT_COLOR = { info: '#38bdf8', success: '#34d399', warning: '#fbbf24', danger: '#fb7185' } as const;

const pill =
  'inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/10 transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/50 [&_svg]:size-3.5';

/** Wniosek w jednym zdaniu, np. „Sprzedaż −20% vs poprzedni miesiąc — sprawdź klientów ze spadkiem”. */
export function HeroInsight({ insight }: { insight: HeroInsightData }) {
  const tone = insight.tone ?? 'info';
  const Icon = INSIGHT_ICON[tone];
  const color = INSIGHT_COLOR[tone];
  const a = insight.action;
  return (
    <div
      className="flex animate-fade-in flex-col items-start gap-2 rounded-xl px-3.5 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 ring-1 ring-white/10 [animation-delay:240ms] [animation-fill-mode:backwards]"
      style={{ background: `linear-gradient(90deg, ${hexToRgba(color, 0.16)}, rgba(255,255,255,0.05) 70%)`, borderLeft: `3px solid ${color}` }}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
        <Icon className="mt-0.5 size-4 shrink-0 sm:mt-0" style={{ color }} aria-hidden />
        <p className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-white/90">{insight.text}</p>
      </div>
      {a &&
        (a.to ? (
          <Link to={a.to} className={pill}>
            {a.label}
            <ArrowRight />
          </Link>
        ) : (
          <button type="button" onClick={a.onClick} className={pill}>
            {a.label}
            <ArrowRight />
          </button>
        ))}
    </div>
  );
}

export function HeroQuickActions({ actions }: { actions: HeroQuickAction[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {actions.map((a) =>
        a.to ? (
          <Link key={a.key} to={a.to} className={pill}>
            {a.icon}
            {a.label}
          </Link>
        ) : (
          <button key={a.key} type="button" onClick={a.onClick} className={pill}>
            {a.icon}
            {a.label}
          </button>
        ),
      )}
    </div>
  );
}

/** „● Na żywo · aktualizacja 11:26 ⟳” — znacznik świeżości danych z opcjonalnym odświeżeniem. */
export function HeroFreshness({ updatedAt, onRefresh, refreshing }: { updatedAt?: Date | number | string; onRefresh?: () => void; refreshing?: boolean }) {
  const { t, locale } = useI18n();
  if (!updatedAt && !onRefresh) return null;
  const time = updatedAt ? new Date(updatedAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false }) : null;
  return (
    <div className="flex items-center gap-2 text-[11px] text-white/50">
      <span className="relative flex size-2" aria-hidden>
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400/60" />
        <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
      </span>
      <span>
        {t('hero.live')}
        {time && ` · ${t('hero.updated', { time })}`}
      </span>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          aria-label={t('hero.refresh')}
          title={t('hero.refresh')}
          className="rounded-md p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <RefreshCw className={cn('size-3.5', refreshing && 'animate-spin')} />
        </button>
      )}
    </div>
  );
}
